<template>
    <v-dialog v-model="internalShow" overlay-opacity="0.6" max-width="480" scrollable @keydown.esc="close">
        <v-card class="order-detail-card pa-0" elevation="6">

            <!-- Black header: logo + security type + full id -->
            <div class="odm-header">
                <img :src="logo" class="odm-logo" :alt="$t('plexus.orderDetail.logoAlt')" />
                <div class="odm-title">{{ item.rfq_security || $t('quoteInfo.spot') }}</div>
            </div>

            <!-- Date -->
            <div class="odm-row odm-white">
                <span class="odm-label">{{ $t('extras.date') }}</span>
                <span class="odm-val">{{ item.rfq_deal_time || '-' }}</span>
            </div>

            <!-- CCY band (yellow) -->
            <div class="odm-row odm-dark-gray">
                <span class="odm-label odm-bold font-weight-black">{{ $t('quoteInfo.parity') }}</span>
                <span class="odm-val odm-bold">{{ item.rfq_ccy ? item.rfq_ccy + ' / BRL' : '-' }}</span>
            </div>

            <!-- Banco -->
            <div class="odm-row odm-light-gray">
                <span class="odm-label">{{ $t('quoteInfo.bank') }}</span>
                <span class="odm-val">{{ item.bank_label || '-' }}</span>
            </div>

            <!-- Side -->
            <div class="odm-row odm-dark-gray odm-white">
                <span class="odm-label">{{ $t('quoteInfo.side') }}</span>
                <span class="odm-val font-weight-black" :class="sideClass">{{ item.rfq_side || '-' }}</span>
            </div>

            <!-- Status -->
            <div class="odm-row odm-light-gray">
                <span class="odm-label">{{ $t('quoteInfo.status') }}</span>
                <span class="odm-val font-weight-medium" :class="statusClass">{{ item.rfq_status || '-' }}</span>
            </div>

            <!-- Valor (amount) -->
            <div class="odm-row odm-dark-gray odm-white">
                <span class="odm-label">{{ $t('plexus.orderDetail.value') }}</span>
                <span class="odm-val">{{ item.rfq_amount || '-' }}</span>
            </div>

            <!-- Spot-specific rows -->
            <template v-if="isSpot">
                <div class="odm-row odm-light-gray">
                    <span class="odm-label">{{ $t('quoteInfo.settlement') }}</span>
                    <span class="odm-val">{{ item.rfq_spotsett || '-' }}</span>
                </div>
                <div class="odm-row odm-dark-gray odm-white">
                    <span class="odm-label">{{ $t('plexus.orderDetail.spotRate') }}</span>
                    <span class="odm-val">{{ item.rfq_deal_px || '-' }}</span>
                </div>
                <div class="odm-row odm-light-gray">
                    <span class="odm-label">{{ $t('quoteInfo.spreadPtax') }}</span>
                    <span class="odm-val">{{ item.rfq_spread_ptax || '-' }}</span>
                </div>
            </template>

            <!-- NDF-specific rows -->
            <template v-else>
                <div class="odm-row odm-light-gray">
                    <span class="odm-label">{{ $t('quoteInfo.maturityText') }}</span>
                    <span class="odm-val">{{ item.rfq_maturity || '-' }}</span>
                </div>
                <div class="odm-row odm-dark-gray odm-white">
                    <span class="odm-label">{{ $t('pricer.forwardRate') }}</span>
                    <span class="odm-val">{{ item.rfq_quote_forward_rate || '-' }}</span>
                </div>
                <div class="odm-row odm-light-gray">
                    <span class="odm-label">{{ $t('pricer.spot') }}</span>
                    <span class="odm-val">{{ item.rfq_quote_spotrate || '-' }}</span>
                </div>
                <div class="odm-row odm-dark-gray odm-white">
                    <span class="odm-label">{{ $t('plexus.orderDetail.fwdPoints') }}</span>
                    <span class="odm-val">{{ item.rfq_quote_fwdpoints || '-' }}</span>
                </div>
            </template>
            <!--
            <div class="odm-row odm-dark-gray  odm-bold">
                <span class="odm-label">
                    Valor em Reais
                    <small class="font-weight-regular odm-muted">(sem despesas)</small>
                </span>
                <div class="odm-currency-val">
                    <span class="odm-currency-symbol">R$</span>
                    <span class="odm-val">{{ brlTotal }}</span>
                </div>
            </div>-->

            <!-- Total para Pagamento (dark yellow, bold) -->
            <div class="odm-row odm-dark-gray odm-bold">
                <span class="odm-label">{{ $t('plexus.orderDetail.totalToPay') }}</span>
                <div class="odm-currency-val">
                    <span class="odm-currency-symbol">{{ $t('plexus.orderDetail.currencySymbol') }}</span>
                    <span class="odm-val">{{ brlTotal }}</span>
                </div>
            </div>

            <!-- Full RFQ ID with copy -->
            <div class="odm-row odm-white odm-id-row">
                <span class="odm-label">{{ $t('quoteInfo.id') }}</span>
                <div class="d-flex align-center" style="gap: 6px; overflow: hidden; min-width: 0;">
                    <span class="odm-val odm-id-text" :title="item.rfq_id">{{ item.rfq_id || '-' }}</span>
                    <v-icon small class="clickable-row flex-shrink-0" @click.stop="copyId">mdi-content-copy</v-icon>
                </div>
            </div>

            <v-divider />

            <!-- Footer actions -->
            <v-card-actions class="pa-3">
                <v-btn small text @click="close" class="text-capitalize">{{$t('instructions.close')}}</v-btn>
                <v-spacer />
                <v-btn
                    small
                    elevation="0"
                    color="primary"
                    :disabled="item.rfq_status !== 'DEAL'"
                    class="text-capitalize white--text"
                    @click="download"
                >
                    <v-icon small color="black" left>mdi-download</v-icon>
                    <span class="black--text text-uppercase">{{ $t('instructions.download') }}</span>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { useSnackbarStore } from "@/store";
import { copyToClipboard as copyText, convertCurrencyToNumber } from "@/utils/utils.js";
import { generateImportacaoPDF } from '@/utils/exportPDF.js';
import { mapActions } from "pinia";
import logoSrc from '@/assets/logo-quartix.io-branco.png';

export default {
    name: 'OrderDetailModal',
    props: {
        value: { type: Boolean, default: false },
        item: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            internalShow: this.value,
            logo: logoSrc,
        };
    },
    watch: {
        value(val) { this.internalShow = val; },
        internalShow(val) { this.$emit('input', val); },
    },
    computed: {
        isSpot() {
            return (this.item.rfq_security || 'FXSPOT') === 'FXSPOT';
        },
        sideClass() {
            return {
                'alive--text': this.item.rfq_side === 'BUY',
                'orangeColor--text': this.item.rfq_side === 'SELL',
            };
        },
        statusClass() {
            return { 'alive--text': this.item.rfq_status === 'DEAL' };
        },
        brlTotal() {
            if (this.item.rfq_status !== 'DEAL') return '-';
            const raw = this.item.rfq_total;
            const num = Number(typeof raw === 'string' ? raw.replace(/,/g, '') : raw);
            if (isNaN(num) || num === 0) return '-';
            return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
    methods: {
        ...mapActions(useSnackbarStore, ["open"]),
        close() {
            this.internalShow = false;
        },
        copyId() {
            copyText(this.item.rfq_id);
            this.open(this.$t('plexusOrderLog.idCopied'));
        },
        async download() {
            if (this.item.rfq_status?.toUpperCase() !== 'DEAL') return;
            await generateImportacaoPDF({
                fileName: String(this.item.rfq_security),
                data: {
                    ...this.item,
                    rfq_amount_number: convertCurrencyToNumber(this.item.rfq_amount),
                    rfq_deal_px: convertCurrencyToNumber(this.item.rfq_deal_px),
                },
            });
        },
    },
};
</script>

<style scoped>
.order-detail-card {
    overflow: hidden;
    border-radius: 6px;
}

/* ── Header ── */
.odm-header {
    background-color: #000;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 14px;
    gap: 10px;
    min-height: 52px;
}

.odm-logo {
    height: 20px;
    flex-shrink: 0;
}

.odm-title {
    flex: 1;
    text-align: end;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.6px;
}

.odm-id-header {
    font-size: 9px;
    color: rgba(255, 255, 255, 0.6);
    font-family: monospace;
    max-width: 130px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
}

/* ── Generic row ── */
.odm-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 14px;
    min-height: 33px;
    font-size: 13px;
    gap: 12px;
}

.odm-label {
    color: #fff;
    white-space: nowrap;
    flex-shrink: 0;
}

.odm-val {
    text-align: right;
    min-width: 0;
}

.odm-bold {
    font-weight: 600;
}

.odm-muted {
    color: #fff;
    font-size: 11px;
}

/* ── Currency value with R$ prefix ── */
.odm-currency-val {
    display: flex;
    align-items: center;
    gap: 8px;
}

.odm-currency-symbol {
    font-size: 11px;
    color: #fff;
    flex-shrink: 0;
}

.odm-bold .odm-currency-symbol {
    color: #fff;
    font-weight: 600;
}

/* ── ID row ── */
.odm-id-row .odm-id-text {
    font-family: monospace;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ── Background variants (matching exportPDF.js colours) ── */
/*.odm-white      { background-color: #15171c; }*/
.odm-gray     { background-color: #FFB000; }
.odm-light-gray { background-color: rgb(21, 23, 28); }
.odm-dark-gray { background-color: rgb(14, 15, 18); }
</style>
